import type {
  CreateAlertRequest,
  CreateAlertResponse,
  FormattedChannel,
  FormattedGetChannelResponse,
  FormattedNotification,
  GetAlertNotificationsResponse,
  GetChannelDetailsResponse,
  GetChannelResponse,
  GetChannelsResponse
} from '@library/types/alerts';
import {
  transformAlerts,
  transformChannels,
  transformNotifications
} from '@bo-utils/alerts';
import { api } from '@bo-services/api';
import { apiPaths } from '@bo-constants/services';

const { admin } = apiPaths;

export const alertsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getAlertNotifications: build.query<Array<FormattedNotification>, void>({
      query: () => ({
        url: `${admin}alerting/notifications`,
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      }),
      transformResponse: (
        response: GetAlertNotificationsResponse | undefined
      ) => transformNotifications(response),
      providesTags: ['AlertNotifications']
    }),
    getAlertNotificationsCount: build.query<
      { data: { unread_count: number } },
      void
    >({
      query: () => ({
        url: `${admin}alerting/notifications/summary`,
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      }),
      providesTags: ['AlertNotificationsCount']
    }),
    createNotification: build.mutation<
      void,
      { channelId: string; messageId: string; destination_user_id: string }
    >({
      query: (payload) => ({
        url: `${admin}alerting/channels/${payload.channelId}/messages/${payload.messageId}/tag`,
        method: 'POST',
        body: payload,
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        },
        validateStatus: (response) => response.status === 200
      }),
      invalidatesTags: ['AlertNotifications', 'AlertNotificationsCount']
    }),
    acknowledgeNotification: build.mutation<void, { id: string }>({
      query: (payload) => ({
        url: `${admin}alerting/notifications/${payload.id}/acknowledge`,
        method: 'POST',
        body: payload,
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        },
        validateStatus: (response, result: CreateAlertResponse) =>
          response && result.success === true
      }),
      invalidatesTags: ['AlertNotifications', 'AlertNotificationsCount']
    }),
    createAlert: build.mutation<CreateAlertResponse, CreateAlertRequest>({
      query: (payload) => ({
        url: `${admin}alerting/channels/${payload.channelId}/messages`,
        method: 'POST',
        body: payload,
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        },
        validateStatus: (response, result: CreateAlertResponse) =>
          response && result.success === true
      }),
      invalidatesTags: ['Alerts']
    }),
    getChannels: build.query<Array<FormattedChannel>, void>({
      query: () => ({
        url: `${admin}alerting/channels`,
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      }),
      transformResponse: (response: GetChannelsResponse | undefined) =>
        transformChannels(response),
      providesTags: ['Channels']
    }),
    getChannelMessages: build.query<
      FormattedGetChannelResponse,
      { channelId: number; page: number; pageSize: number; query?: string }
    >({
      query: ({ channelId, page, pageSize, query }) => ({
        url: `${admin}alerting/channels/${channelId}/messages`,
        method: 'GET',
        params: {
          page,
          per_page: pageSize,
          query
        },
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      }),
      transformResponse: (response: GetChannelResponse | undefined) =>
        transformAlerts(response),
      providesTags: ['Alerts', 'Channels']
    }),
    getChannelDetails: build.query<
      GetChannelDetailsResponse,
      { channelId: number }
    >({
      query: ({ channelId }) => ({
        url: `${admin}alerting/channels/${channelId}`,
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      }),
      providesTags: ['ChannelUsers']
    }),
    readChannel: build.mutation<void, { id: number }>({
      query: (payload) => ({
        url: `${admin}alerting/channels/${payload.id}/mark_read`,
        method: 'POST',
        body: payload,
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        },
        validateStatus: (response, result: CreateAlertResponse) =>
          response && result.success === true
      })
    }),
    subscribeChannel: build.mutation<void, { id: number }>({
      query: (payload) => ({
        url: `${admin}alerting/channels/${payload.id}/subscribe`,
        method: 'POST',
        body: payload,
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        },
        validateStatus: (response, result: CreateAlertResponse) =>
          response && result.success === true
      }),
      invalidatesTags: ['ChannelUsers']
    }),
    unsubscribeChannel: build.mutation<void, { id: number }>({
      query: (payload) => ({
        url: `${admin}alerting/channels/${payload.id}/unsubscribe`,
        method: 'POST',
        body: payload,
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        },
        validateStatus: (response, result: CreateAlertResponse) =>
          response && result.success === true
      }),
      invalidatesTags: ['ChannelUsers']
    })
  })
});

export const {
  useGetAlertNotificationsQuery,
  useGetAlertNotificationsCountQuery,
  useAcknowledgeNotificationMutation,
  useCreateAlertMutation,
  useGetChannelMessagesQuery,
  useGetChannelDetailsQuery,
  useGetChannelsQuery,
  useReadChannelMutation,
  useSubscribeChannelMutation,
  useUnsubscribeChannelMutation,
  useCreateNotificationMutation
} = alertsApi;
